import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { clearRoutes, setLoading, setHeaderLoading } from "../../store/slices/travelSlice";
import { ArrowLeftRight, Train, Plane, Bus, Plus, Minus, LayoutGrid, X, Calendar, ChevronDown, MapPin } from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";
import { toast } from 'react-toastify';
import { FEATURES } from '../../config/features';
import MobileSearchCard from './MobileSearchCard';

/* Desktop-only; phones use MobileSearchCard's own calendar, so this chunk never loads there */
const DatePicker = lazy(() => import("react-datepicker"));

/* Phones get the full-screen-picker search (MobileSearchCard).
   Matches the existing ≤768px mobile breakpoint in home2.css. */
const MOBILE_QUERY = '(max-width: 768px)';
const useIsMobile = () => {
    const get = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
    const [m, setM] = useState(get);
    useEffect(() => {
        if (!window.matchMedia) return;
        const mq = window.matchMedia(MOBILE_QUERY);
        const on = () => setM(mq.matches);
        on();
        mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
        return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
    }, []);
    return m;
};

/* Shared by mount-time resolution, search-time resolution and the mobile picker */
const POPULAR_LOCS = [
    { name: "Indira Gandhi Intl Airport", code: "DEL", city: "Delhi", type: "airport", label: "Delhi (DEL)", subLabel: "IGI Airport", lat: 28.5562, lng: 77.1000 },
    { name: "New Delhi", code: "NDLS", city: "Delhi", type: "station", label: "New Delhi (NDLS)", subLabel: "Railway Station", lat: 28.6141, lng: 77.2144 },
    { name: "Chhatrapati Shivaji Intl", code: "BOM", city: "Mumbai", type: "airport", label: "Mumbai (BOM)", subLabel: "International Airport", lat: 19.0887, lng: 72.8679 },
    { name: "Mumbai Central", code: "MMCT", city: "Mumbai", type: "station", label: "Mumbai Central (MMCT)", subLabel: "Railway Station", lat: 18.9698, lng: 72.8193 },
    { name: "Kempegowda Intl Airport", code: "BLR", city: "Bangalore", type: "airport", label: "Bangalore (BLR)", subLabel: "International Airport", lat: 13.1989, lng: 77.7068 },
    { name: "Ahmedabad Airport", code: "AMD", city: "Ahmedabad", type: "airport", label: "Ahmedabad (AMD)", subLabel: "Airport", lat: 23.0734, lng: 72.6266 },
    { name: "Ahmedabad Junction", code: "ADI", city: "Ahmedabad", type: "station", label: "Ahmedabad (ADI)", subLabel: "Railway Station", lat: 23.0273, lng: 72.5950 },
    { name: "KSR Bengaluru City", code: "SBC", city: "Bengaluru", type: "station", label: "Bengaluru (SBC)", subLabel: "Railway Station", lat: 12.9774, lng: 77.5728 },
    { name: "Chennai Central", code: "MAS", city: "Chennai", type: "station", label: "Chennai Central (MAS)", subLabel: "Railway Station", lat: 13.0827, lng: 80.2707 },
    { name: "Netaji Subhash Chandra Bose Intl", code: "CCU", city: "Kolkata", type: "airport", label: "Kolkata (CCU)", subLabel: "International Airport", lat: 22.6547, lng: 88.4467 },
    { name: "Pune Junction", code: "PUNE", city: "Pune", type: "station", label: "Pune Jn (PUNE)", subLabel: "Railway Station", lat: 18.5289, lng: 73.8744 },
    { name: "Hyderabad Deccan", code: "HYB", city: "Hyderabad", type: "station", label: "Hyderabad (HYB)", subLabel: "Railway Station", lat: 17.3916, lng: 78.4684 }
];

const FormSearchSection = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    // Helper to get initial search data from localStorage
    const getInitialSearchData = () => {
        const savedSearch = localStorage.getItem("last_form_search");
        if (savedSearch) {
            try {
                return JSON.parse(savedSearch);
            } catch (e) {
                console.error("Error parsing saved search:", e);
            }
        }
        return null;
    };

    const initialData = getInitialSearchData();

    const [aiSearchInput, setAiSearchInput] = useState("");
    const [activeModes, setActiveModes] = useState(initialData?.activeModes || ['All']);
    const [showTravelersModal, setShowTravelersModal] = useState(false);
    const [passengerCounts, setPassengerCounts] = useState(initialData?.passengerCounts || {
        adults: 1,
        children: 0,
        infants: 0
    });
    const [seatClasses, setSeatClasses] = useState(initialData?.seatClasses || ['Economy', '3A']);
    const [travelDate, setTravelDate] = useState(() => {
        if (initialData?.travelDate) {
            const parsedDate = new Date(initialData.travelDate);
            if (parsedDate >= new Date().setHours(0, 0, 0, 0)) {
                return parsedDate;
            }
        }
        return null;
    });
    const [fromLocation, setFromLocation] = useState(initialData?.fromLocation || "");
    const [toLocation, setToLocation] = useState(initialData?.toLocation || "");
    const [fromLocationData, setFromLocationData] = useState(initialData?.fromLocationData || null);
    const [toLocationData, setToLocationData] = useState(initialData?.toLocationData || null);
    const [nonStopOnly, setNonStopOnly] = useState(initialData?.nonStopOnly ?? false);

    const travelersRef = React.useRef(null);
    const [swapSpin, setSwapSpin] = useState(false);

    // Smart Resolution of locations on mount (for persistent search)
    React.useEffect(() => {

        const resolve = (txt, data) => {
            if (txt && !data) {
                // Try to match popular first
                const match = POPULAR_LOCS.find(l =>
                    l.city.toLowerCase() === txt.toLowerCase() ||
                    l.label.toLowerCase() === txt.toLowerCase() ||
                    l.name.toLowerCase() === txt.toLowerCase()
                );
                if (match) return match;

                // Fallback: If it's a typed value from a successful search, 
                // wrap it in a placeholder so the chip background (bg) appears.
                // The results page will handle the name resolution.
                return { label: txt, city: txt, type: 'search-match' };
            }
            return data;
        };

        const resFrom = resolve(fromLocation, fromLocationData);
        if (resFrom && resFrom !== fromLocationData) {
            setFromLocationData(resFrom);
            if (resFrom.label) setFromLocation(resFrom.label);
        }

        const resTo = resolve(toLocation, toLocationData);
        if (resTo && resTo !== toLocationData) {
            setToLocationData(resTo);
            if (resTo.label) setToLocation(resTo.label);
        }
    }, []);

    React.useEffect(() => {
        // Sync travelers and seat classes logic if needed
    }, [activeModes]);

    const dropdownRef = useRef(null);

    React.useEffect(() => {
        const handleOutside = (event) => {
            const inTrigger = travelersRef.current && travelersRef.current.contains(event.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
            if (!inTrigger && !inDropdown) {
                setShowTravelersModal(false);
            }
        };

        // mousedown for desktop, touchend for mobile (touchstart races with onClick)
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("touchend", handleOutside, { passive: true });
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("touchend", handleOutside);
        };
    }, []);

    // Toggle Mode Logic
    const toggleMode = (mode) => {
        if (mode === 'All') {
            setActiveModes(['All']);
            return;
        }

        setActiveModes(prev => {
            let newModes = [...prev];

            // If currently All is selected, clear it and start with the new mode
            if (newModes.includes('All')) {
                newModes = [];
            }

            if (newModes.includes(mode)) {
                // Deselect logic: prevent deselecting the last mode (optional, but good UX)
                // or replicate "allow empty" which defaults to something? 
                // User requirement: "all else can be selected independently"
                newModes = newModes.filter(m => m !== mode);
            } else {
                newModes.push(mode);
            }

            // If we ended up empty, default back to 'All' or just leave it?
            // "only ALL when selected no other can be left selected"
            // Let's enforce at least one selection? Or default to All if empty.
            if (newModes.length === 0) return ['All'];

            return newModes;
        });
    };

    // Toggle seat class selection (multi-select)
    const toggleSeatClass = (cls) => {
        setSeatClasses(prev => {
            const current = Array.isArray(prev) ? prev : [];
            const isTrainClass = ['1A', '2A', '3A', 'SL'].includes(cls);
            const isFlightClass = ['Economy', 'Premium', 'Business', 'First'].includes(cls);

            if (isTrainClass) {
                const withoutTrain = current.filter(c => !['1A', '2A', '3A', 'SL'].includes(c));
                return [...withoutTrain, cls];
            }
            if (isFlightClass) {
                const withoutFlight = current.filter(c => !['Economy', 'Premium', 'Business', 'First'].includes(c));
                return [...withoutFlight, cls];
            }
            return current;
        });
    };

    // Lock body scroll when travelers modal is open
    React.useEffect(() => {
        if (showTravelersModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showTravelersModal]);

    const isFormValid = fromLocation && toLocation && travelDate;

    /* Search → /search-form (shared by the desktop button and MobileSearchCard) */
    const runSearch = () => {
        if (!fromLocation || String(fromLocation).trim() === '') {
            toast.error('Please enter departure city', { position: 'top-center' });
            return;
        }
        if (!toLocation || String(toLocation).trim() === '') {
            toast.error('Please enter destination city', { position: 'top-center' });
            return;
        }
        if (!travelDate) {
            toast.error('Please select travel date', { position: 'top-center' });
            return;
        }

        if (String(fromLocation).trim().toLowerCase() === String(toLocation).trim().toLowerCase()) {
            toast.error('Origin and Destination cannot be the same', { position: 'top-center' });
            return;
        }

        // Smart pick before search if data is missing (POPULAR_LOCS is module-level)

        let finalFromData = fromLocationData;
        let finalToData = toLocationData;
        let finalFromText = fromLocation;
        let finalToText = toLocation;

        if (fromLocation && !fromLocationData) {
            const match = POPULAR_LOCS.find(l =>
                l.city.toLowerCase() === fromLocation.toLowerCase() ||
                l.label.toLowerCase() === fromLocation.toLowerCase()
            );
            if (match) {
                finalFromData = match;
                finalFromText = match.label;
            }
        }
        if (toLocation && !toLocationData) {
            const match = POPULAR_LOCS.find(l =>
                l.city.toLowerCase() === toLocation.toLowerCase() ||
                l.label.toLowerCase() === toLocation.toLowerCase()
            );
            if (match) {
                finalToData = match;
                finalToText = match.label;
            }
        }

        // Advanced check: Same city but different station/airport names
        if (finalFromData && finalToData) {
            const normalizeCity = (c) => {
                const city = (c || '').trim().toLowerCase();
                if (city === 'new delhi') return 'delhi';
                return city;
            };

            const originCity = normalizeCity(finalFromData.city);
            const destCity = normalizeCity(finalToData.city);
            const originCode = (finalFromData.code || '').trim().toLowerCase();
            const destCode = (finalToData.code || '').trim().toLowerCase();

            // If city names match OR airport/station codes match, it's the same location effectively
            if ((originCity && destCity && originCity === destCity) ||
                (originCode && destCode && originCode === destCode)) {
                toast.error('Origin and Destination cannot be in the same city', { position: 'top-center' });
                return;
            }
        }

        dispatch(clearRoutes());
        dispatch(setLoading(true));
        dispatch(setHeaderLoading(true));

        const params = new URLSearchParams({
            from: finalFromText,
            to: finalToText,
            date: travelDate ? (() => {
                const d = new Date(travelDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            })() : '',
            adults: passengerCounts.adults,
            children: passengerCounts.children,
            infants: passengerCounts.infants,
            seatClasses: seatClasses.join(','),
            modes: activeModes.join(','),
            nonstop: nonStopOnly ? '1' : '0'
        });
        navigate(`/search-form?${params.toString()}`, {
            state: {
                fromLocation: finalFromText,
                toLocation: finalToText,
                fromLocationData: finalFromData,
                toLocationData: finalToData,
                travelDate: travelDate ? travelDate.toISOString() : null,
                passengerCounts,
                seatClasses,
                activeModes,
                nonStopOnly: nonStopOnly
            }
        });

        // Save search data to localStorage for persistence
        localStorage.setItem("last_form_search", JSON.stringify({
            fromLocation: finalFromText,
            toLocation: finalToText,
            fromLocationData: finalFromData,
            toLocationData: finalToData,
            travelDate: travelDate ? travelDate.toISOString() : null,
            passengerCounts,
            seatClasses,
            activeModes,
            nonStopOnly
        }));
    };

    const handleAISearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput: aiSearchInput } });
    };

    return (
        <section className={`home-search-section${isMobile ? ' rcm-host' : ''}`}>
            {/* ── Phones: full-screen pickers, same state + same search ── */}
            {isMobile && (
                <MobileSearchCard
                    fromLocation={fromLocation}
                    toLocation={toLocation}
                    fromLocationData={fromLocationData}
                    toLocationData={toLocationData}
                    setFrom={(item) => { setFromLocation(item.label); setFromLocationData(item); }}
                    setTo={(item) => { setToLocation(item.label); setToLocationData(item); }}
                    swap={() => {
                        const temp = fromLocation;
                        const tempData = fromLocationData;
                        setFromLocation(toLocation);
                        setFromLocationData(toLocationData);
                        setToLocation(temp);
                        setToLocationData(tempData);
                    }}
                    travelDate={travelDate}
                    setTravelDate={setTravelDate}
                    activeModes={activeModes}
                    toggleMode={toggleMode}
                    passengerCounts={passengerCounts}
                    seatClasses={seatClasses}
                    openTravelers={() => setShowTravelersModal(true)}
                    nonStopOnly={nonStopOnly}
                    setNonStopOnly={setNonStopOnly}
                    popularLocs={POPULAR_LOCS}
                    onSearch={runSearch}
                />
            )}

            {/* ── Tablet & desktop: unchanged ── */}
            {!isMobile && (<>
            <style>{FORM_CSS}</style>
            {/* AI Search Bar - Always Visible */}
            <div className="search-bar" style={{ margin: 0 }}>
                <form onSubmit={handleAISearch} style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <div className="search-input" style={{ flex: 1 }}>
                        <img src="/Star1.svg" alt="AI" />
                        <input
                            type="text"
                            value={aiSearchInput}
                            onChange={e => setAiSearchInput(e.target.value)}
                            placeholder="Darbhanga se Mumbai jane ke liye kal ki date ke liye routes batao"
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                width: '100%'
                            }}
                        />
                    </div>
                    <button className="search-button" type="submit">
                        <img src="/send.svg" alt="Send" />
                    </button>
                </form>
            </div>

            {/* Divider */}
            <div className="form-divider-or">
                <div className="divider-line"></div>
                OR
                <div className="divider-line"></div>
            </div>

            {/* Advanced Form Search */}
            <div className="form-search-container">
                <div className="home-search-bar-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <div className="home-search-bar">
                        {/* From / To: two boxes with a gap; the swap button sits in the gap */}
                        <div className="search-field home-search-main-field rc-od-desk">
                            <LocationBox
                                kind="from"
                                label="From"
                                placeholder="City, station or airport"
                                text={fromLocation}
                                data={fromLocationData}
                                setText={setFromLocation}
                                setData={setFromLocationData}
                            />
                            <button
                                type="button"
                                className={`rc-od-swap${swapSpin ? ' spin' : ''}`}
                                aria-label="Swap origin and destination"
                                title="Swap"
                                disabled={!fromLocation && !toLocation}
                                onClick={() => {
                                    setSwapSpin(v => !v);
                                    const temp = fromLocation;
                                    const tempData = fromLocationData;
                                    setFromLocation(toLocation);
                                    setFromLocationData(toLocationData);
                                    setToLocation(temp);
                                    setToLocationData(tempData);
                                }}
                            >
                                <ArrowLeftRight size={17} strokeWidth={2.3} />
                            </button>
                            <LocationBox
                                kind="to"
                                label="To"
                                placeholder="City, station or airport"
                                text={toLocation}
                                data={toLocationData}
                                setText={setToLocation}
                                setData={setToLocationData}
                            />
                            <style>{OD_CSS}</style>
                        </div>

                        <div className="search-field-sep"></div>

                        <div className="search-field date-picker-container">
                            <span className="mobile-label">Departure</span>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
                                <Suspense fallback={<span className="search-field-input" style={{ color: '#94A3B8' }}>Select date</span>}>
                                <DatePicker
                                    selected={travelDate}
                                    onChange={(date) => setTravelDate(date)}
                                    dateFormat="MMM dd/eee"
                                    className="search-field-input"
                                    placeholderText="Select date"
                                    minDate={new Date()}
                                    isClearable={false}
                                    popperPlacement="bottom"
                                />
                                </Suspense>
                                <Calendar size={18} color="#64748B" className="mobile-only-icon" style={{ flexShrink: 0 }} />
                            </div>
                        </div>

                        <div className="search-field-sep"></div>

                        <div
                            className="search-field travelers-field"
                            ref={travelersRef}
                            onClick={() => setShowTravelersModal(true)}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="mobile-label">Travelers & Class</span>
                            <div className="search-field-input" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span style={{
                                    flex: 1, textAlign: 'left', minWidth: 0,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                }}>
                                    {(() => {
                                        const showTrain = activeModes.includes('Train') || activeModes.includes('All');
                                        const showFlight = activeModes.includes('Flight') || activeModes.includes('All');
                                        const fl = seatClasses.filter(c => ['Economy', 'Premium', 'Business', 'First'].includes(c));
                                        const tr = seatClasses.filter(c => ['1A', '2A', '3A', 'SL'].includes(c));
                                        const { adults = 1, children = 0, infants = 0 } = passengerCounts;
                                        const pax = [`${adults} adult${adults > 1 ? 's' : ''}`];
                                        if (children > 0) pax.push(`${children} child${children > 1 ? 'ren' : ''}`);
                                        if (infants > 0) pax.push(`${infants} infant${infants > 1 ? 's' : ''}`);
                                        return (
                                            <>
                                                <span>{pax.join(', ')}</span>
                                                {showFlight && fl.length > 0 && (<><span style={{ color: '#CBD5E1' }}>·</span><Plane size={15} style={{ flexShrink: 0, color: '#64748B' }} /><span>{fl.join(', ')}</span></>)}
                                                {showTrain && tr.length > 0 && (<><span style={{ color: '#CBD5E1' }}>·</span><Train size={15} style={{ flexShrink: 0, color: '#64748B' }} /><span>{tr.join(', ')}</span></>)}
                                            </>
                                        );
                                    })()}
                                </span>
                                <span style={{
                                    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3,
                                    marginLeft: 14, paddingLeft: 12, borderLeft: '1px solid #E2E8F0',
                                    color: '#FF4E01', fontFamily: "'Inter', sans-serif",
                                    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                }}>
                                    Change
                                    <ChevronDown size={14} style={{ flexShrink: 0 }} />
                                </span>
                            </div>

                            
                        </div>
                    </div>
                </div>

                <div className="modes-container">
                    <span>Preferred modes</span>
                    <div className="modes-tabs">
                        <button className={`mode-btn ${activeModes.includes('All') ? 'active' : ''}`} onClick={() => toggleMode('All')}>
                            <LayoutGrid size={20} /> All
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Train') ? 'active' : ''}`} onClick={() => toggleMode('Train')}>
                            <Train size={20} /> Train
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Flight') ? 'active' : ''}`} onClick={() => toggleMode('Flight')}>
                            <Plane size={20} /> Flight
                        </button>
                        {FEATURES.ENABLE_BUS && (
                            <button className={`mode-btn ${activeModes.includes('Bus') ? 'active' : ''}`} onClick={() => toggleMode('Bus')}>
                                <Bus size={20} /> Bus
                            </button>
                        )}
                    </div>

                    <div className="options-row">
                        <label className="non-stop">
                            <input
                                type="checkbox"
                                checked={nonStopOnly}
                                onChange={(e) => setNonStopOnly(e.target.checked)}
                            />
                            <span>Non stop only</span>
                        </label>
                    </div>
                    <button
                        className={`primary-search-btn ${!isFormValid ? 'disabled' : ''}`}
                        disabled={!isFormValid}
                        onClick={runSearch}
                    >
                        Search Routes
                    </button>
                </div>
            </div>
            </>)}

        {/* ── Travelers bottom sheet ──
            Portalled to <body>: inside the hero it is trapped under the hero's
            stacking context, so the navbar and the lead-bot bubble drew over it. */}
        {showTravelersModal && createPortal(
            <>
                {/* Backdrop */}
                <div
                    onClick={() => setShowTravelersModal(false)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        zIndex: 2147482999,
                    }}
                />
                {/* Sheet */}
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '480px',
                        background: '#fff',
                        borderRadius: '20px 20px 0 0',
                        padding: '0 0 calc(24px + env(safe-area-inset-bottom))',
                        zIndex: 2147483000,
                        maxHeight: '85vh',
                        overflowY: 'auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Handle */}
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#E2E8F0' }} />
                    </div>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 16px' }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' }}>Travelers & Class</h2>
                        <button
                            onClick={() => setShowTravelersModal(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        >
                            <X size={22} color="#0F172A" />
                        </button>
                    </div>

                    <div style={{ padding: '0 20px' }}>
                        {/* Passenger counts */}
                        {[
                            { key: 'adults', label: 'Adults', age: '18+' },
                            { key: 'children', label: 'Children', age: '2-11' },
                            { key: 'infants', label: 'Infants', age: 'under 2' }
                        ].map(({ key, label, age }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #F1F5F9' }}>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>{label}</div>
                                    <div style={{ fontSize: 13, color: '#94A3B8' }}>{age}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPassengerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) })); }}
                                        style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    ><Minus size={16} /></button>
                                    <span style={{ fontSize: 18, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{passengerCounts[key] || 0}</span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPassengerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 })); }}
                                        style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    ><Plus size={16} /></button>
                                </div>
                            </div>
                        ))}

                        {/* Flight classes */}
                        {(activeModes.includes('Flight') || activeModes.includes('All')) && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 12 }}>Flight Seat Class</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {['Economy', 'Premium', 'Business', 'First'].map(cls => {
                                        const isSel = seatClasses.includes(cls);
                                        return (
                                            <div key={cls} onClick={(e) => { e.stopPropagation(); toggleSeatClass(cls); }}
                                                style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${isSel ? '#FF4E01' : '#CBD5E1'}`, background: isSel ? '#FFF7F4' : '#fff', color: isSel ? '#FF4E01' : '#475569', fontWeight: isSel ? 700 : 500, fontSize: 14, cursor: 'pointer' }}>
                                                {isSel ? '✓ ' : ''}{cls}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Train classes */}
                        {(activeModes.includes('Train') || activeModes.includes('All')) && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 12 }}>Train Seat Class</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {['1A', '2A', '3A', 'SL'].map(cls => {
                                        const isSel = seatClasses.includes(cls);
                                        return (
                                            <div key={cls} onClick={(e) => { e.stopPropagation(); toggleSeatClass(cls); }}
                                                style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${isSel ? '#FF4E01' : '#CBD5E1'}`, background: isSel ? '#FFF7F4' : '#fff', color: isSel ? '#FF4E01' : '#475569', fontWeight: isSel ? 700 : 500, fontSize: 14, cursor: 'pointer' }}>
                                                {isSel ? '✓ ' : ''}{cls}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setPassengerCounts({ adults: 1, children: 0, infants: 0 }); setSeatClasses(['Economy', '3A']); }}
                                style={{ flex: 1, height: 48, border: '1.5px solid #CBD5E1', borderRadius: 10, background: '#fff', color: '#475569', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                            >Clear</button>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowTravelersModal(false); }}
                                style={{ flex: 2, height: 48, border: 'none', borderRadius: 10, background: '#0F172A', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                            >Apply</button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        )}

        </section>
    );
};

/* ── Desktop From / To field ───────────────────────────────────────────────
   No box of its own: the search bar is the only outline. From and To are two segments split
   by a thin divider, with the swap button sitting on the divider.
   Picked: label, place name, and a sub line (code · airport / station / area), like the big OTAs.
   Click a picked box to search again; leaving it empty puts the previous pick back.
   Not picked: the autocomplete input. Free text (a small town) still works, as before. */
const odName = (txt) => String(txt || '').split('(')[0].trim();
const odCode = (txt, data) => data?.code || (String(txt || '').match(/\(([^)]+)\)/) || [])[1] || '';
const odKind = (data) => {
    if (!data) return '';
    if (data.type === 'airport') return data.subLabel && !/^airport$/i.test(data.subLabel) ? data.subLabel : 'Airport';
    if (data.type === 'station') return data.subLabel && !/railway station/i.test(data.subLabel) ? `${data.subLabel} · Railway station` : 'Railway station';
    if (data.type === 'bus' || data.type === 'bus_stop') return 'Bus stop';
    if (data.type === 'search-match') return '';
    return data.subLabel || '';
};
const OdIcon = ({ type }) => {
    const Icon = type === 'airport' ? Plane : type === 'station' ? Train : MapPin;
    return <Icon size={14} strokeWidth={2.3} aria-hidden="true" />;
};

const LocationBox = ({ kind, label, placeholder, text, data, setText, setData }) => {
    const boxRef = useRef(null);
    const prevRef = useRef(null);            // pick to restore if the user clicks in and leaves empty
    const latest = useRef({ text, data });
    latest.current = { text, data };
    const [editing, setEditing] = useState(false);

    const startEdit = () => {
        prevRef.current = { text, data };
        setEditing(true);
        setText('');
        setData(null);
    };
    // Focus once the input has rendered
    useEffect(() => {
        if (!editing) return;
        const el = boxRef.current?.querySelector('input');
        if (el) el.focus();
    }, [editing]);

    // Leaving the box: the suggestion list closes after 200ms, so check after that
    const onBlur = () => {
        setTimeout(() => {
            if (boxRef.current && boxRef.current.contains(document.activeElement)) return;
            const cur = latest.current;
            if (!cur.text && !cur.data && prevRef.current) {
                setText(prevRef.current.text);
                setData(prevRef.current.data);
            }
            prevRef.current = null;
            setEditing(false);
        }, 260);
    };

    const picked = !!data && !editing;
    const name = odName(text);
    const code = odCode(text, data);
    const sub = odKind(data);

    return (
        <div
            ref={boxRef}
            className={`rc-od-box ${kind}${picked ? ' picked' : ''}`}
            onBlur={onBlur}
            onClick={picked ? startEdit : () => boxRef.current?.querySelector('input')?.focus()}
            role={picked ? 'button' : undefined}
            tabIndex={picked ? 0 : undefined}
            aria-label={picked ? `${label}: ${name}. Change` : undefined}
            onKeyDown={picked ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(); } } : undefined}
        >
            <span className="rc-od-label">{label}</span>
            {picked ? (
                <>
                    <span className="rc-od-name" title={text}>{name}</span>
                    <span className="rc-od-sub">
                        <OdIcon type={data.type} />
                        {code && <b className="rc-od-code">{code}</b>}
                        {sub && <span className="rc-od-kind">{sub}</span>}
                    </span>
                    <button
                        type="button"
                        className="rc-od-clear"
                        aria-label={`Clear ${label.toLowerCase()}`}
                        onClick={(e) => { e.stopPropagation(); prevRef.current = null; setText(''); setData(null); setEditing(true); }}
                    >
                        <X size={14} strokeWidth={2.5} />
                    </button>
                </>
            ) : (
                <>
                    <TravelAutocompleteInput
                        className="rc-od-input"
                        anchorRef={boxRef}
                        minWidth={380}
                        placeholder={placeholder}
                        value={text}
                        onChange={(val) => { setText(val); setData(null); }}
                        onSelect={(item) => { prevRef.current = null; setText(item.label); setData(item); setEditing(false); }}
                    />
                </>
            )}
        </div>
    );
};

// Desktop/tablet only: AI hint size and the "Preferred modes" buttons
const FORM_CSS = `
@media (min-width: 769px){
  /* AI chat hint: bigger and easier to read */
  .home-search-section .search-bar .search-input input{
    font-size:16.5px !important;font-weight:500;color:#0F172A;font-family:'Inter',sans-serif;
  }
  .home-search-section .search-bar .search-input input::placeholder{color:#64748B;font-size:16.5px;opacity:1}

  /* Mode buttons: dark text when off, deep orange on a soft tint when on (readable either way) */
  .home-search-section .modes-tabs .mode-btn{
    display:inline-flex;align-items:center;gap:7px;
    background:#fff !important;color:#334155 !important;border:1.5px solid #E2E8F0 !important;
    font-family:'Inter',sans-serif;font-weight:600;font-size:14.5px;
    transition:border-color .15s, background .15s, color .15s;
  }
  .home-search-section .modes-tabs .mode-btn svg{color:#64748B;stroke:currentColor}
  .home-search-section .modes-tabs .mode-btn:hover{border-color:#CBD5E1 !important;background:#F8FAFC !important}
  .home-search-section .modes-tabs .mode-btn.active{
    background:#FFF4EE !important;color:#C2410C !important;border-color:#FF4E01 !important;font-weight:700;
  }
  .home-search-section .modes-tabs .mode-btn.active svg{color:#FF4E01}
  .home-search-section .modes-tabs .mode-btn:focus-visible{outline:2px solid #FF4E01;outline-offset:2px}
}
`;

// Desktop/tablet only (phones render MobileSearchCard instead, so none of this reaches them)
const OD_CSS = `
@media (min-width: 769px){
  /* Field widths: From/To gets the room, date only needs "Oct 24/Fri" */
  .home-search-bar .rc-od-desk.search-field.home-search-main-field{flex:2.4 1 0 !important;min-width:0}
  .home-search-bar .search-field.date-picker-container{flex:0 0 156px !important;width:156px !important;max-width:156px !important;min-width:0}
  .home-search-bar .search-field.date-picker-container .react-datepicker-wrapper,
  .home-search-bar .search-field.date-picker-container input{width:100% !important;min-width:0}
  .home-search-bar .search-field.travelers-field{flex:1.3 1 0 !important;min-width:0}
}
.rc-od-desk.search-field.home-search-main-field{
  display:flex !important;align-items:stretch;gap:0;position:relative;
  padding:0 !important;background:transparent;border:0;box-shadow:none;
}
.rc-od-box{
  position:relative;flex:1 1 0;min-width:0;display:flex;flex-direction:column;justify-content:center;
  gap:3px;padding:8px 16px;min-height:62px;box-sizing:border-box;
  border:0;border-radius:12px;background:transparent;cursor:text;text-align:left;
  transition:background .15s;
}
.rc-od-box.from{padding-right:30px}
.rc-od-box.to{padding-left:30px}
.rc-od-box:hover,.rc-od-box:focus-within{background:#F8FAFC}
.rc-od-box.picked{cursor:pointer}
.rc-od-box.picked:focus-visible{outline:2px solid #FF4E01;outline-offset:-2px}
/* the one divider between From and To; the swap button sits on it */
.rc-od-box.from::after{content:"";position:absolute;right:0;top:20%;bottom:20%;width:1px;background:#E2E8F0}
.rc-od-label{font-family:'Inter',sans-serif;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#94A3B8;line-height:1}
.rc-od-name{font-family:'Inter',sans-serif;font-size:18px;font-weight:700;color:#0F172A;line-height:1.25;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding-right:20px}
.rc-od-sub{display:flex;align-items:center;gap:5px;font-family:'Inter',sans-serif;font-size:12px;color:#64748B;
  line-height:1.3;min-width:0;white-space:nowrap;overflow:hidden}
.rc-od-sub svg{flex-shrink:0;color:#94A3B8}
.rc-od-code{font-weight:700;color:#334155;letter-spacing:.02em}
.rc-od-kind{overflow:hidden;text-overflow:ellipsis}
.rc-od-input{width:100%;border:0 !important;outline:0 !important;background:transparent !important;padding:0 !important;
  margin:0;height:auto !important;box-shadow:none !important;
  font-family:'Inter',sans-serif;font-size:18px !important;font-weight:600;color:#0F172A;line-height:1.3}
.rc-od-input::placeholder{color:#94A3B8;font-weight:500;font-size:15.5px}
.rc-od-clear{position:absolute;top:50%;transform:translateY(-50%);width:24px;height:24px;border-radius:50%;border:0;
  background:#E2E8F0;color:#475569;display:flex;align-items:center;justify-content:center;cursor:pointer;
  opacity:0;transition:opacity .15s,background .15s;padding:0}
.rc-od-box.from .rc-od-clear{right:30px}
.rc-od-box.to .rc-od-clear{right:10px}
.rc-od-box.picked:hover .rc-od-clear,.rc-od-box.picked:focus-within .rc-od-clear{opacity:1}
.rc-od-clear:hover{background:#CBD5E1}
.rc-od-swap{
  position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:5;
  width:34px;height:34px;border-radius:50%;padding:0;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  background:#fff;color:#FF4E01;border:1px solid #E2E8F0;
  box-shadow:0 1px 4px rgba(15,23,42,.08);
  transition:transform .3s cubic-bezier(.4,0,.2,1), border-color .15s, background .15s;
  -webkit-tap-highlight-color:transparent;touch-action:manipulation;
}
.rc-od-swap:hover:not(:disabled){border-color:#FF4E01;background:#FFF7F3}
.rc-od-swap:focus-visible{outline:2px solid #FF4E01;outline-offset:2px}
.rc-od-swap.spin{transform:translate(-50%,-50%) rotate(180deg)}
.rc-od-swap:disabled{color:#CBD5E1;cursor:default}
@media (prefers-reduced-motion: reduce){.rc-od-swap{transition:none}}
`;

export default FormSearchSection;
